import { from, Observable, of, switchMap, throwError } from "rxjs";
import { Service } from "typedi";
import { ENVIRONMENT } from "../../../../environments/environment";
import {
  GooglePayProductionEnvironment,
  GooglePayTestEnvironment,
} from "../../../../integrations/google-pay/models/IGooglePayConfig";
import { IGooglePaySessionPaymentsClient } from "../../../../integrations/google-pay/models/IGooglePaySessionPaymentsClient";
import { IGooglePayIsReadyToPayRequest } from "../../../../integrations/google-pay/models/IGooglePayIsReadyToPayRequest";
import { IConfig } from "../../../../shared/model/config/IConfig";
import { IGooglePaySdkProvider } from "./IGooglePaySdkProvider";
import { DomScriptUtils } from "../../../../application/core/shared/dom-methods/DomScriptUtils";
import {
  hasDynamicPriceIntent,
  hasPaymentAuthorizationIntent,
} from "../GooglePayNativeCallbackIntents";

@Service()
export class GooglePaySdkProvider implements IGooglePaySdkProvider {
  private static readonly notReadyError =
    "The GooglePay library is unable to provide the necessary components " +
    "to accept the payment. This may be an issue with the configuration " +
    "of the browser or device.";

  private readonly scriptAddress = ENVIRONMENT.GOOGLE_PAY.GOOGLE_PAY_URL;
  private readonly scriptTarget = "head";

  setupSdk$(config: IConfig): Observable<IGooglePaySessionPaymentsClient> {
    return this.insertGooglePayLibrary().pipe(
      switchMap(() => {
        const client = this.createPaymentsClient(config);
        return this.assertReadyToPay(client, config);
      }),
    );
  }

  private insertGooglePayLibrary(): Observable<Element> {
    return DomScriptUtils.insertScript(this.scriptTarget, {
      src: this.scriptAddress,
    });
  }

  private createPaymentsClient(
    config: IConfig,
  ): IGooglePaySessionPaymentsClient {
    const paymentConfig = config.googlePay.paymentRequest;

    const clientConfig: {
      environment: string;
      paymentDataCallbacks?: google.payments.api.PaymentDataCallbacks;
    } = {
      environment: this.resolveEnvironment(paymentConfig.environment),
      ...this.buildClientCallbacks(paymentConfig),
    };

    return new (
      window as unknown as IWindow
    ).google.payments.api.PaymentsClient(clientConfig);
  }

  /**
   * Selectively attaches callbacks to the PaymentsClient based on intent type:
   *
   * - onPaymentDataChanged is only set when dynamic price intents exist.
   *   Without them, we fire this callback manually before the wallet sheet
   *   opens (see GooglePayPrePaymentCallbackHandler).
   * - onPaymentAuthorized is set whenever PAYMENT_AUTHORIZATION is present,
   *   regardless of whether dynamic intents exist.
   */
  private buildClientCallbacks(
    paymentRequest: IConfig["googlePay"]["paymentRequest"],
  ): { paymentDataCallbacks?: google.payments.api.PaymentDataCallbacks } {
    const callbacks = paymentRequest.paymentDataCallbacks;

    if (!callbacks) {
      return {};
    }

    const intents = paymentRequest.callbackIntents;
    const clientCallbacks: Partial<google.payments.api.PaymentDataCallbacks> =
      {};

    if (hasDynamicPriceIntent(intents) && callbacks.onPaymentDataChanged) {
      clientCallbacks.onPaymentDataChanged = callbacks.onPaymentDataChanged;
    }

    if (
      hasPaymentAuthorizationIntent(intents) &&
      callbacks.onPaymentAuthorized
    ) {
      clientCallbacks.onPaymentAuthorized = callbacks.onPaymentAuthorized;
    }

    if (Object.keys(clientCallbacks).length === 0) {
      return {};
    }

    return {
      paymentDataCallbacks:
        clientCallbacks as google.payments.api.PaymentDataCallbacks,
    };
  }

  private resolveEnvironment(configuredEnvironment?: string): string {
    if (configuredEnvironment) {
      return configuredEnvironment;
    }

    return ENVIRONMENT.production
      ? GooglePayProductionEnvironment
      : GooglePayTestEnvironment;
  }

  private assertReadyToPay(
    client: IGooglePaySessionPaymentsClient,
    config: IConfig,
  ): Observable<IGooglePaySessionPaymentsClient> {
    return from(
      client.isReadyToPay(this.buildIsReadyToPayRequest(config)),
    ).pipe(
      switchMap((response) => {
        if (!response.result) {
          return throwError(
            () => new Error(GooglePaySdkProvider.notReadyError),
          );
        }
        return of(client);
      }),
    );
  }

  private buildIsReadyToPayRequest(
    config: IConfig,
  ): IGooglePayIsReadyToPayRequest {
    const { apiVersion, apiVersionMinor, allowedPaymentMethods } =
      config.googlePay.paymentRequest;

    return { apiVersion, apiVersionMinor, allowedPaymentMethods };
  }
}
